import { CsvExportButton, useCsvExport } from '../components/CsvExport';
// Leads per mockups/02-leads.html: pipeline shelves (one lane per open
// status), drag-and-drop between shelves (PATCH status), slim vitals,
// Pipeline/Won/Lost/All tabs, closed-status chip strip, search, and the
// new-lead modal (09-new-lead).

import { useViewState } from '../lib/useViewState';
import { oneOf, str, nullableStr } from '../lib/viewState';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { api } from '../lib/api';
import { reportError } from '../lib/errorEvents';
import { melbourneDate, melbourneToday } from '../../shared/dates';
import {
  LEAD_STATUSES,
  LEAD_CAMPAIGN_MAX,
  LEAD_OPEN_STATUSES,
  LEAD_ACQUISITION_LABELS,
  LEAD_ACQUISITIONS,
  LEAD_REQUEST_TYPE_LABELS,
  LEAD_SOURCE_LABELS,
  LEAD_SOURCES,
  LEAD_STATUS_LABELS,
  LEAD_TYPE_LABELS,
  LEAD_TYPES,
  type ClientContactRow,
  type ClientWithCounts,
  type LeadAcquisition,
  type LeadRequestType,
  type LeadStatus,
  type LeadWithNames,
} from '../../shared/types';
import {
  addDays,
  BulkActionBar,
  FilterBar,
  formatDate,
  formatDayMonth,
  formatMoney,
  Modal,
  PageHeader,
  parseMoney,
  Pill,
  SearchBox,
  Seg,
  SelectField,
  SkeletonRows,
  SortButton,
  Tabs,
  VS,
  VitalsSlim,
  type SortOption,
  type SortValue,
} from '../components/primitives';
import { ViewChips } from '../components/ViewChips';
import { useBulkSelect } from '../lib/useBulkSelect';
import { useListKeyboard } from '../lib/listKeyboard';

import { useIsMobile } from '../lib/useIsMobile';
import { canLockInLead, leadFollowupMeta, lockedInStart } from '../lib/leadStage';
import { LeadStageSheet, LeadStartDateModal, leadStageColors } from '../components/leadStageSheet';
import { SwipeRow } from '../components/SwipeRow';
import { PageMenu } from '../components/PageMenu';
import { FilterSheet } from '../components/FilterSheet';
import { SearchIcon, FilterIcon, DotsIcon, LeadSourceIcon } from '../components/icons';
import { ConvertLeadModal } from './LeadDetail';

const SHELVES: { status: LeadStatus; dot: string; card: string; glow?: boolean; note?: string }[] = [
  { status: 'enquiry', dot: 'pd-lead', card: 'lc-enquiry' },
  { status: 'discovery_call', dot: 'pd-pre', card: 'lc-discovery' },
  { status: 'proposal_creation', dot: 'pd-shoot', card: 'lc-creation' },
  { status: 'proposal_sent', dot: 'pd-post', card: 'lc-sent' },
  { status: 'negotiation', dot: 'pd-post', card: 'lc-negotiation', glow: true },
  // Confirmed work that isn't a project yet — its own value, outside Pipeline.
  { status: 'locked_in', dot: 'pd-locked', card: 'lc-locked', note: 'Confirmed · not a project yet' },
];

/** Every status the Pipeline tab shows: open deals, Locked in and Not now. */
function onPipeline(status: LeadStatus): boolean {
  return LEAD_OPEN_STATUSES.includes(status) || status === 'locked_in' || status === 'postponed';
}

// Board filter (round three §4): strict match, so untagged leads only show
// under All.
type AcquisitionFilter = 'all' | LeadAcquisition;
const ACQUISITION_OPTIONS: readonly { value: AcquisitionFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  ...LEAD_ACQUISITIONS.map((value) => ({ value, label: LEAD_ACQUISITION_LABELS[value] })),
];
const isAcquisition = (v: unknown): v is LeadAcquisition => (LEAD_ACQUISITIONS as readonly unknown[]).includes(v);

const CLOSED: LeadStatus[] = ['won', 'lost', 'ghosted', 'void'];

type View = 'pipeline' | 'all' | LeadStatus;

// ── Sorting (feedback r2 §3) ─────────────────────────────────────────────
// One comparator drives both the pipeline shelves and the closed lists, so
// the button label always describes what is on screen.
const SORT_OPTIONS: SortOption[] = [
  { key: 'closed', label: 'Closed', menuLabel: 'Closed date', natural: 'desc', naturalLabel: 'newest first' },
  { key: 'enquiry', label: 'Enquiry', menuLabel: 'Enquiry date', natural: 'desc', naturalLabel: 'newest first' },
  { key: 'value', label: 'Value', natural: 'desc', naturalLabel: 'highest first' },
  { key: 'name', label: 'Name', natural: 'asc', naturalLabel: 'A–Z' },
];

// On the pipeline the useful enquiry read is the other way round — longest
// waiting at the top of its shelf — so that tab gets its own natural label.
const PIPELINE_SORT_OPTIONS: SortOption[] = SORT_OPTIONS.map((o) =>
  o.key === 'enquiry' ? { ...o, natural: 'asc', naturalLabel: 'oldest first' } : o,
);

/** Each tab opens on the order that tab is for. */
function defaultSortFor(view: View): SortValue {
  if (view === 'pipeline') return { key: 'enquiry', dir: 'asc' };
  // Ghosted leads are never stamped with a closed date, so that list sorts
  // by enquiry like All; the other closed lists sort by when they closed.
  if (view === 'all' || view === 'ghosted') return { key: 'enquiry', dir: 'desc' };
  return { key: 'closed', dir: 'desc' };
}

/** Leads imported without an enquiry date fall back to when the row was
    created, so the column never reads (or sorts) as missing. */
function leadEnquiryDate(l: LeadWithNames): string {
  return l.enquiry_date ?? melbourneDate(l.created_at);
}

function sortKeyOf(l: LeadWithNames, key: string): string | number | null {
  switch (key) {
    case 'closed': return l.closed_date;
    case 'enquiry': return leadEnquiryDate(l);
    case 'value': return l.value_cents;
    case 'name': return l.name || null;
    default: return null;
  }
}

/** Missing values (open leads have no closed date, no quoted value) always
    sink, whichever way the arrow points; ties fall back to newest-created. */
function compareLeads(sort: SortValue) {
  const flip = sort.dir === 'asc' ? 1 : -1;
  return (a: LeadWithNames, b: LeadWithNames) => {
    const ka = sortKeyOf(a, sort.key);
    const kb = sortKeyOf(b, sort.key);
    if (ka === null || kb === null) {
      if (ka !== null) return -1;
      if (kb !== null) return 1;
    } else {
      const cmp = sort.key === 'name'
        ? String(ka).localeCompare(String(kb), undefined, { sensitivity: 'base' })
        : ka < kb ? -1 : ka > kb ? 1 : 0;
      if (cmp !== 0) return cmp * flip;
    }
    return b.created_at - a.created_at;
  };
}

function serializeSort(sort: SortValue): string {
  return `${sort.key}:${sort.dir}`;
}

function parseSort(raw: string | undefined): SortValue | null {
  if (!raw) return null;
  const [key, dir] = raw.split(':');
  if (!key || !SORT_OPTIONS.some((o) => o.key === key)) return null;
  if (dir !== 'asc' && dir !== 'desc') return null;
  return { key, dir };
}

/** Second date column's label — the closed date means something different
    per list. */
function closedLabelFor(view: View): string {
  return view === 'won' ? 'won' : view === 'lost' ? 'lost' : 'closed';
}

function fuLabel(iso: string | null, today: string): { text: string; cls: string } | null {
  if (!iso) return null;
  if (iso === today) return { text: 'f/u today', cls: ' today' };
  if (iso < today) {
    const days = Math.round((Date.parse(today) - Date.parse(iso)) / 86400000);
    return { text: `${days}d overdue`, cls: ' overdue' };
  }
  return { text: `f/u ${formatDayMonth(iso)}`, cls: '' };
}

/** Outlined Quote / Proposal tag — beside the card value, under a phone row's. */
export function LeadRequestTag({ type }: { type: LeadRequestType | null }) {
  return type ? <span className={`lead-req ${type}`} title={`Requested a ${type}`}>{LEAD_REQUEST_TYPE_LABELS[type]}</span> : null;
}

/** Source glyph, then the small teal Paid tag. Organic carries no mark. */
function LeadSourceTags({ lead, size }: { lead: LeadWithNames; size?: number }) {
  return <>
    <LeadSourceIcon source={lead.source} size={size} />
    {lead.acquisition === 'paid' && <span className="lead-paid" title={lead.campaign ? `Paid · ${lead.campaign}` : 'Paid acquisition'}>Paid</span>}
  </>;
}

export function LeadCardBody({ lead, today }: { lead: LeadWithNames; today: string }) {
  const isPostponed = lead.status === 'postponed';
  const start = lead.status === 'locked_in' ? lockedInStart(lead.resurface_date, today) : null;
  const fu = isPostponed || start ? null : fuLabel(lead.followup_date, today);
  const hasCorner = (lead.source !== null && lead.source !== 'other') || lead.acquisition === 'paid';
  return (
    <>
      {hasCorner && <span className="lead-corner"><LeadSourceTags lead={lead} /></span>}
      <div className="lead-title" title={lead.name}>{lead.name}</div>
      <div className="lead-company" title={lead.company ?? lead.contact_name ?? '—'}>
        {lead.company ?? lead.contact_name ?? '—'}
      </div>
      <div className="lead-foot">
        <span className={`lead-value${lead.value_cents === null ? ' none' : ''}`}>
          {lead.value_cents === null ? '—' : formatMoney(lead.value_cents)}
        </span>
        <LeadRequestTag type={lead.request_type} />
        {start
          ? <span className={`lead-start${start.due ? ' due' : ''}`}>{start.text}</span>
          : isPostponed
          ? lead.resurface_date && (
              <span className="lead-resurface">resurfaces {formatDayMonth(lead.resurface_date)}</span>
            )
          : fu && <span className={`lead-fu${fu.cls}`}>{fu.text}</span>}
      </div>
    </>
  );
}

function DraggableCard({
  lead,
  cardClass,
  today,
  hidden,
  onOpen,
}: {
  lead: LeadWithNames;
  cardClass: string;
  today: string;
  hidden: boolean;
  onOpen: () => void;
}) {
  const { attributes, listeners, setNodeRef } = useDraggable({ id: lead.id });
  return (
    <div
      ref={setNodeRef}
      className={`lead-card ${cardClass}`}
      style={hidden ? { opacity: 0.35 } : undefined}
      onClick={onOpen}
      {...listeners}
      {...attributes}
    >
      <LeadCardBody lead={lead} today={today} />
    </div>
  );
}

function Shelf({
  status,
  title,
  note,
  dot,
  glow,
  cardClass,
  leads,
  today,
  draggingId,
  isOver,
  onOpen,
}: {
  status: LeadStatus;
  title?: string;
  note?: string;
  dot: string;
  glow?: boolean;
  cardClass: string;
  leads: LeadWithNames[];
  today: string;
  draggingId: string | null;
  isOver: boolean;
  onOpen: (id: string) => void;
}) {
  const { setNodeRef } = useDroppable({ id: status });
  const value = leads.reduce((sum, l) => sum + (l.value_cents ?? 0), 0);
  const showSlot = isOver && !leads.some((l) => l.id === draggingId);
  return (
    <div className="shelf" ref={setNodeRef}>
      <div className="shelf-head">
        <span
          className={`phase-dot ${dot}`}
          style={glow ? { boxShadow: '0 0 8px var(--accent-glow)' } : undefined}
        />
        <span className="shelf-title">{title ?? LEAD_STATUS_LABELS[status]}</span>
        <span className="shelf-count">{leads.length}</span>
        {note && <span className="shelf-note">{note}</span>}
        {value > 0 && <span className="shelf-value">{formatMoney(value)}</span>}
      </div>
      {leads.length === 0 && !showSlot ? (
        <div className="empty-shelf">No leads here.</div>
      ) : (
        <div className="shelf-cards">
          {showSlot && <div className="drop-slot" />}
          {leads.map((lead) => (
            <DraggableCard
              key={lead.id}
              lead={lead}
              cardClass={cardClass}
              today={today}
              hidden={lead.id === draggingId}
              onOpen={() => onOpen(lead.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export const leadsViewCodec = {
  view: oneOf('view', ['pipeline', 'all', ...LEAD_STATUSES], 'pipeline'), query: str('q'),
  source: nullableStr('source'),
  lead_type: nullableStr('type'),
  acquisition: oneOf('acq', ['all', ...LEAD_ACQUISITIONS], 'all'),
  // null means this tab's default; pages resolve it with defaultSortFor(view).
  sort: { ...nullableStr('sort'), dec: (raw: string) => {
    const parsed = parseSort(raw);
    return parsed && serializeSort(parsed) === raw ? raw : undefined;
  } },
};

export default function Leads() {
  const { initial, publish } = useViewState(leadsViewCodec, state => {
    setView(state.view);
    setSort(parseSort(state.sort ?? undefined) ?? defaultSortFor(state.view));
    setQuery(state.query);
    setFilters({ source: state.source, lead_type: state.lead_type });
    setAcquisition(state.acquisition);
  });
  const isMobile = useIsMobile();
  const [showFilters, setShowFilters] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [rowMenu, setRowMenu] = useState<LeadWithNames | null>(null);
  const [stageLead, setStageLead] = useState<LeadWithNames | null>(null);
  const [convertLead, setConvertLead] = useState<LeadWithNames | null>(null);
  const [draftFilters, setDraftFilters] = useState<Record<string, string | null>>({ source: null, lead_type: null });
  const [draftSort, setDraftSort] = useState<SortValue>({ key: 'enquiry', dir: 'asc' });
  const [draftQuery, setDraftQuery] = useState('');
  const [draftAcquisition, setDraftAcquisition] = useState<AcquisitionFilter>('all');
  const [lockLead, setLockLead] = useState<LeadWithNames | null>(null);
  const [leads, setLeads] = useState<LeadWithNames[]>([]);
  const [view, setView] = useState<View>(() => initial.view);
  const [sort, setSort] = useState<SortValue>(() => parseSort(initial.sort ?? undefined) ?? defaultSortFor(initial.view));
  const [query, setQuery] = useState(() => initial.query);
  const [filters, setFilters] = useState<Record<string, string | null>>(() => ({ source: initial.source, lead_type: initial.lead_type }));
  const [acquisition, setAcquisition] = useState<AcquisitionFilter>(() => initial.acquisition);

  useEffect(() => {
    publish({
      view,
      query,
      sort: serializeSort(sort) === serializeSort(defaultSortFor(view)) ? null : serializeSort(sort),
      source: filters.source,
      lead_type: filters.lead_type,
      acquisition,
    });
  }, [publish, view, query, sort, filters.source, filters.lead_type, acquisition]);
  const [showNew, setShowNew] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overShelf, setOverShelf] = useState<LeadStatus | null>(null);
  // Quiet loading state (Phase 1.3) — skeleton until the first fetch settles.
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();
  const today = melbourneToday();

  // Filter dropdowns + saved views (Phase 1.12) — source + lead type.
  const filterFields = [
    { key: 'source', label: 'Source', items: LEAD_SOURCES.map((s) => ({ value: s, label: LEAD_SOURCE_LABELS[s] })) },
    { key: 'lead_type', label: 'Type', items: LEAD_TYPES.map((t) => ({ value: t, label: LEAD_TYPE_LABELS[t] })) },
  ];
  // The sort only rides along when it differs from the tab's default:
  // paramsEqual (shared/savedViews.ts) is exact serialised equality, so an
  // unconditional key would stop every already-saved view from matching.
  const sortIsDefault = serializeSort(sort) === serializeSort(defaultSortFor(view));
  const viewState = { view, query, ...filters, acquisition: acquisition === 'all' ? null : acquisition, sort: sortIsDefault ? null : serializeSort(sort) };
  const isDefaultView =
    view === 'pipeline' && sortIsDefault && !query && !filters.source && !filters.lead_type && acquisition === 'all';
  const applyView = (p: Record<string, string>) => {
    const nextView = (p.view as View) || 'pipeline';
    setView(nextView);
    setSort(parseSort(p.sort) ?? defaultSortFor(nextView));
    setQuery(p.query ?? '');
    setFilters({ source: p.source ?? null, lead_type: p.lead_type ?? null });
    setAcquisition(isAcquisition(p.acquisition) ? p.acquisition : 'all');
  };
  /** Switching list — from the tabs or the closed strip — resets the sort to
      that list's default. Filters and search never touch it. */
  const changeView = (nextView: View) => {
    setView(nextView);
    setSort(defaultSortFor(nextView));
  };

  const listSeq = useRef(0);
  const fetchLeads = useCallback(() => {
    const seq = ++listSeq.current;
    api<{ leads: LeadWithNames[] }>('/api/admin/leads')
      .then((d) => { if (seq === listSeq.current) setLeads(d.leads); })
      .catch(() => {})
      .finally(() => { if (seq === listSeq.current) setLoaded(true); });
  }, []);
  const mutatedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refresh = useCallback(() => {
    if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
    mutatedTimer.current = null;
    fetchLeads();
  }, [fetchLeads]);
  useEffect(() => {
    fetchLeads();
    const onMutated = () => {
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      mutatedTimer.current = setTimeout(fetchLeads, 250);
    };
    window.addEventListener('ss:mutated', onMutated);
    return () => {
      window.removeEventListener('ss:mutated', onMutated);
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      listSeq.current++;
    };
  }, [fetchLeads]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // The acquisition filter scopes the whole board, vitals included, so
  // Pipeline and Won follow it (approved).
  const scoped = useMemo(
    () => (acquisition === 'all' ? leads : leads.filter((l) => l.acquisition === acquisition)),
    [leads, acquisition],
  );
  const filtered = useMemo(() => {
    let list = scoped;
    if (filters.source) list = list.filter((l) => l.source === filters.source);
    if (filters.lead_type) list = list.filter((l) => l.lead_type === filters.lead_type);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((l) =>
        [l.name, l.company, l.contact_name, l.contact_email, l.client_name]
          .filter(Boolean)
          .some((s) => (s as string).toLowerCase().includes(q)),
      );
    }
    return list;
  }, [scoped, query, filters]);

  const byStatus = useMemo(() => {
    const map = new Map<LeadStatus, LeadWithNames[]>();
    for (const l of filtered) {
      const list = map.get(l.status) ?? [];
      list.push(l);
      map.set(l.status, list);
    }
    // Shelves follow the Sort button too, defaulting to longest-waiting first.
    const compare = compareLeads(sort);
    for (const list of map.values()) list.sort(compare);
    return map;
  }, [filtered, sort]);

  // Slim vitals — over all leads in the acquisition scope, not the search subset.
  const open = scoped.filter((l) => LEAD_OPEN_STATUSES.includes(l.status));
  const pipelineValue = open.reduce((s, l) => s + (l.value_cents ?? 0), 0);
  const lockedValue = scoped.filter((l) => l.status === 'locked_in').reduce((s, l) => s + (l.value_cents ?? 0), 0);
  const followupsDue = open.filter((l) => l.followup_date && l.followup_date <= today).length;
  const fyStart =
    today >= `${today.slice(0, 4)}-07-01`
      ? `${today.slice(0, 4)}-07-01`
      : `${Number(today.slice(0, 4)) - 1}-07-01`;
  const wonFY = scoped
    .filter((l) => l.status === 'won' && l.closed_date && l.closed_date >= fyStart)
    .reduce((s, l) => s + (l.value_cents ?? 0), 0);

  const onDragStart = (e: DragStartEvent) => setDraggingId(String(e.active.id));
  const onDragOver = (e: DragOverEvent) => setOverShelf((e.over?.id as LeadStatus) ?? null);
  const onDragEnd = async (e: DragEndEvent) => {
    const id = String(e.active.id);
    const target = e.over?.id as LeadStatus | undefined;
    setDraggingId(null);
    setOverShelf(null);
    const lead = leads.find((l) => l.id === id);
    if (!lead || !target || lead.status === target) return;
    // Locked in asks for a start date before anything moves.
    if (target === 'locked_in') {
      if (canLockInLead(lead)) setLockLead(lead);
      return;
    }
    const prev = leads;
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status: target } : l)));
    try {
      await api(`/api/admin/leads/${id}`, { method: 'PATCH', json: { status: target } });
      refresh();
    } catch {
      setLeads(prev);
    }
  };

  const draggingLead = draggingId ? leads.find((l) => l.id === draggingId) : null;
  const draggingClass = draggingLead
    ? draggingLead.status === 'postponed'
      ? 'lc-postponed'
      : (SHELVES.find((s) => s.status === draggingLead.status)?.card ?? 'lc-enquiry')
    : '';

  const listRows =
    view === 'all'
      ? [...filtered].sort(compareLeads(sort))
      : view !== 'pipeline'
        ? filtered.filter((l) => l.status === view).sort(compareLeads(sort))
        : [];

  const csvExport = useCsvExport('leads', { view, query, ...filters, acquisition: acquisition === 'all' ? null : acquisition, sort: serializeSort(sort) }, {
    count: view === 'pipeline' ? filtered.filter(l => onPipeline(l.status)).length : listRows.length,
  });

  // Bulk-select + keyboard (Phase 1.13) — list view only, never the kanban
  // drag cards. Passing an empty id list while on the pipeline tab both
  // disables j/k/x there and auto-prunes any lingering selection.
  const bulkIds = view === 'pipeline' ? [] : listRows.map((l) => l.id);
  const bulk = useBulkSelect(bulkIds);
  const kb = useListKeyboard({
    ids: bulkIds,
    enabled: !isMobile && !showNew,
    onToggle: bulk.toggle,
    onOpen: (id) => navigate(`/leads/${id}`),
    onClear: bulk.clear,
  });
  const runBulkTrash = async () => {
    const ids = bulk.ids;
    if (ids.length === 0) return;
    try {
      await api('/api/admin/leads/bulk', { method: 'POST', json: { ids, action: 'trash' } });
    } catch (error) {
      reportError(error, 'Could not move the selected leads to trash.');
      return;
    }
    bulk.clear();
    refresh();
  };

  const doneFollowup = async (lead: LeadWithNames) => {
    try {
      await api(`/api/admin/leads/${lead.id}`, { method: 'PATCH', json: { followup_date: null } });
      refresh();
    } catch (error) { reportError(error, 'Could not complete the follow-up.'); }
  };
  const draftCount = leads.filter(l =>
    (draftAcquisition === 'all' || l.acquisition === draftAcquisition) &&
    (!draftFilters.source || l.source === draftFilters.source) &&
    (!draftFilters.lead_type || l.lead_type === draftFilters.lead_type) &&
    (!draftQuery.trim() || [l.name, l.company, l.contact_name, l.contact_email, l.client_name].some(v => v?.toLowerCase().includes(draftQuery.trim().toLowerCase()))) &&
    (view === 'all' || (view === 'pipeline' ? onPipeline(l.status) : l.status === view))
  ).length;
  const mobileRow = (lead: LeadWithNames, pipeline: boolean) => {
    const meta = leadFollowupMeta(lead, today);
    return <SwipeRow key={lead.id} disabled={!pipeline} leftActions={[
      { label: 'F/u done', icon: '✓', tone: 'ok', onSelect: () => void doneFollowup(lead) },
      { label: 'Stage', icon: '⇄', tone: 'gold', onSelect: () => setStageLead(lead) },
    ]}>
      <div className="lead-mobile-row">
        <button className="lead-row-open" onClick={() => navigate(`/leads/${lead.id}`)}>
          <span className="lrow-main"><span className="lead-row-title">{lead.name}<span className="lead-title-tags"><LeadSourceTags lead={lead} size={13} /></span></span>
            <span className="lead-row-meta">{lead.company ?? lead.contact_name ?? '—'} · <span className={pipeline ? meta.tone : undefined}>{pipeline ? meta.text : `enq ${formatDayMonth(leadEnquiryDate(lead))}`}</span></span>
          </span>
          <span className="lead-row-right"><span className="lead-row-money">{lead.value_cents === null ? '—' : formatMoney(lead.value_cents)}</span>
            {pipeline && <LeadRequestTag type={lead.request_type} />}
            {!pipeline && (view === 'all' ? <Pill>{LEAD_STATUS_LABELS[lead.status]}</Pill> : <span style={{ color: view === 'won' ? 'var(--ok)' : undefined }}>{closedLabelFor(view)} {lead.closed_date ? formatDayMonth(lead.closed_date) : '—'}</span>)}
          </span>
        </button>
        <button className="lead-row-more" aria-label={`Actions for ${lead.name}`} onClick={() => setRowMenu(lead)}><DotsIcon /></button>
      </div>
    </SwipeRow>;
  };

  return (
    <>
      <PageHeader title="Leads" count={leads.length} mobileActions={isMobile ? [
        { key: 'search', label: 'Search', icon: <SearchIcon />, onPress: () => window.dispatchEvent(new Event('ss:open-search')) },
        { key: 'filter', label: 'Filter', icon: <FilterIcon />, active: !isDefaultView, onPress: () => { setDraftFilters({ ...filters }); setDraftSort(sort); setDraftQuery(query); setDraftAcquisition(acquisition); setShowFilters(true); } },
        { key: 'menu', label: 'More lead actions', icon: <DotsIcon />, onPress: () => setShowMenu(true) },
      ] : undefined} />

      {isMobile ? <>
        <div className="mtabs">{([
          { value: 'pipeline', label: 'Pipeline', count: open.length },
          { value: 'won', label: 'Won', count: scoped.filter(l => l.status === 'won').length },
          { value: 'lost', label: 'Lost', count: scoped.filter(l => l.status === 'lost').length },
          { value: 'all', label: 'All' },
        ] as const).map(tab => <button key={tab.value} className={`t${view === tab.value ? ' on' : ''}`} onClick={() => changeView(tab.value)}>{tab.label}{'count' in tab && <span className="c">{tab.count}</span>}</button>)}</div>
        {query && <div className="chiprow"><button className="c on" onClick={() => setQuery('')}>“{query}” ×</button></div>}
        {acquisition !== 'all' && <div className="chiprow lead-acq-chips"><button className="c on" onClick={() => setAcquisition('all')}>{LEAD_ACQUISITION_LABELS[acquisition]} only ×</button></div>}
        <div className="lead-mobile-stats"><span>{view === 'pipeline' ? `PIPELINE · ${formatMoney(pipelineValue)} · ${open.length} OPEN` : `WON THIS FY · ${formatMoney(wonFY)}`}</span>
          <span className={view === 'pipeline' ? 'danger' : ''}>{view === 'pipeline' ? `${followupsDue} FOLLOW-UPS DUE` : `Sort · ${SORT_OPTIONS.find(o => o.key === sort.key)?.menuLabel ?? SORT_OPTIONS.find(o => o.key === sort.key)?.label} ${sort.dir === 'asc' ? '↑' : '↓'}`}</span></div>
      </> : <div className="toolbar leads-toolbar">
        <Tabs
          options={[
            { value: 'pipeline', label: 'Pipeline' },
            { value: 'won', label: 'Won' },
            { value: 'lost', label: 'Lost' },
            { value: 'all', label: 'All' },
          ]}
          value={view === 'pipeline' || view === 'won' || view === 'lost' || view === 'all' ? view : 'all'}
          onChange={(v) => changeView(v as View)}
        />
        {/* Acquisition rides in "+ Filter" (chip "Acquisition: Outbound ×")
            so the toolbar stays on one line at laptop widths. */}
        <FilterBar
          fields={[...filterFields, { key: 'acquisition', label: 'Acquisition', items: ACQUISITION_OPTIONS.filter((o) => o.value !== 'all') }]}
          active={{ ...filters, acquisition: acquisition === 'all' ? null : acquisition }}
          onChange={(key, value) => key === 'acquisition'
            ? setAcquisition(isAcquisition(value) ? value : 'all')
            : setFilters((f) => ({ ...f, [key]: value }))}
        />
        <SortButton
          options={view === 'pipeline' ? PIPELINE_SORT_OPTIONS : SORT_OPTIONS}
          value={sort}
          onChange={setSort}
        />
        <SearchBox placeholder="Search name, company, contact…" value={query} onChange={setQuery} />
        <CsvExportButton state={csvExport} />
        <button className="btn primary" onClick={() => setShowNew(true)}>
          + New lead
        </button>
      </div>}

      <ViewChips page="leads" current={viewState} isDefault={isDefaultView} onApply={applyView} />

      <div className="page-body">
        <div className={isMobile ? "content wide lead-mobile-list" : "content wide"}>
          {!loaded ? (
            <SkeletonRows />
          ) : (
            <>
              {!isMobile && <VitalsSlim>
                <VS label="Pipeline" value={formatMoney(pipelineValue)} tone="gold" />
                <VS label="Locked in" value={formatMoney(lockedValue)} tone="ok" title="Confirmed work not yet converted. Excluded from Pipeline." />
                <VS label="Open" value={open.length} />
                <VS label="Follow-ups due" value={followupsDue} tone={followupsDue > 0 ? 'danger' : undefined} />
                <VS label="Won this FY" value={formatMoney(wonFY)} tone="accent" />
              </VitalsSlim>}

              {isMobile ? (view === 'pipeline' ? <>
                {[...SHELVES, { status: 'postponed' as const }].map(shelf => {
                  const rows = byStatus.get(shelf.status) ?? [];
                  return <section key={shelf.status} className="lead-mobile-group">
                    <div className="lead-group-head"><span className="lead-stage-dot" style={{ background: leadStageColors[shelf.status] }} /><span>{shelf.status === 'postponed' ? 'Not now' : LEAD_STATUS_LABELS[shelf.status]}</span><span className="lead-group-count">{rows.length}</span><span className="lead-group-value">{rows.length > 0 ? formatMoney(rows.reduce((sum, l) => sum + (l.value_cents ?? 0), 0)) : ''}</span></div>
                    {rows.length ? rows.map(l => mobileRow(l, true)) : <div className="lead-group-empty">No leads here.</div>}
                  </section>;
                })}
              </> : <>{listRows.map(l => mobileRow(l, false))}{!listRows.length && <div className="lead-group-empty">Nothing here.</div>}</>) : view === 'pipeline' ? (
                <>
                  <DndContext
                    sensors={sensors}
                    onDragStart={onDragStart}
                    onDragOver={onDragOver}
                    onDragEnd={onDragEnd}
                  >
                    {SHELVES.map((shelf) => (
                      <Shelf
                        key={shelf.status}
                        status={shelf.status}
                        note={shelf.note}
                        dot={shelf.dot}
                        glow={shelf.glow}
                        cardClass={shelf.card}
                        leads={byStatus.get(shelf.status) ?? []}
                        today={today}
                        draggingId={draggingId}
                        isOver={overShelf === shelf.status}
                        onOpen={(id) => navigate(`/leads/${id}`)}
                      />
                    ))}
                    <Shelf
                      key="postponed"
                      status="postponed"
                      title="Not now"
                      dot="pd-shoot"
                      cardClass="lc-postponed"
                      leads={byStatus.get('postponed') ?? []}
                      today={today}
                      draggingId={draggingId}
                      isOver={overShelf === 'postponed'}
                      onOpen={(id) => navigate(`/leads/${id}`)}
                    />
                    <DragOverlay>
                      {draggingLead && (
                        <div className={`lead-card ${draggingClass} dragging`}>
                          <LeadCardBody lead={draggingLead} today={today} />
                        </div>
                      )}
                    </DragOverlay>
                  </DndContext>

                  <div className="closed-strip">
                    <span className="closed-label">Closed</span>
                    {CLOSED.map((status) => (
                      <button
                        key={status}
                        className={`closed-chip${status === 'won' ? ' won' : ''}`}
                        onClick={() => changeView(status)}
                      >
                        {LEAD_STATUS_LABELS[status]}{' '}
                        <span className="n">{scoped.filter((l) => l.status === status).length}</span>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="group" style={{ marginTop: 18 }}>
                  <div className="group-head">
                    <span className="group-title">
                      {view === 'all' ? 'All leads' : LEAD_STATUS_LABELS[view as LeadStatus]}
                    </span>
                    <span className="group-count">{listRows.length}</span>
                  </div>
                  {listRows.map((l) => (
                    <div
                      className={`row${kb.focusedId === l.id ? ' kbd-focus' : ''}`}
                      key={l.id}
                      onClick={() => navigate(`/leads/${l.id}`)}
                    >
                      <span
                        className={`bulk-check${bulk.isSelected(l.id) ? ' on' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          bulk.toggle(l.id);
                        }}
                      />
                      <span className="row-title" title={l.name}>{l.name}</span>
                      {(l.company ?? l.contact_name) && (
                        <span className="row-context" title={l.company ?? l.contact_name ?? undefined}>
                          {l.company ?? l.contact_name}
                        </span>
                      )}
                      <span className="row-spacer" />
                      {view === 'all' && <Pill>{LEAD_STATUS_LABELS[l.status]}</Pill>}
                      {l.value_cents !== null && (
                        <span className="row-meta gold">{formatMoney(l.value_cents)}</span>
                      )}
                      <span className={`dmeta${sort.key === 'enquiry' ? ' hi' : ''}`}>
                        enq <span className="d">{formatDayMonth(leadEnquiryDate(l))}</span>
                      </span>
                      <span className={`dmeta${sort.key === 'closed' ? ' hi' : ''}`}>
                        {closedLabelFor(view)}{' '}
                        <span className="d">{l.closed_date ? formatDayMonth(l.closed_date) : '—'}</span>
                      </span>
                    </div>
                  ))}
                  {listRows.length === 0 && <div className="empty-note">Nothing here.</div>}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <BulkActionBar
        count={bulk.count}
        actions={[{ key: 'trash', label: 'Trash', danger: true, onClick: runBulkTrash }]}
        onClear={bulk.clear}
      />

      {isMobile && <>
        <PageMenu open={showMenu} onClose={() => setShowMenu(false)} title="Leads" items={[{ key: 'new', label: 'New lead', onSelect: () => setShowNew(true) }, csvExport.item]} />
        <PageMenu open={!!rowMenu} onClose={() => setRowMenu(null)} title={rowMenu?.name} items={rowMenu ? [
          ...(rowMenu.followup_date ? [{ key: 'done', label: 'Follow-up done', onSelect: () => void doneFollowup(rowMenu) }] : []),
          { key: 'stage', label: 'Move to stage', onSelect: () => setStageLead(rowMenu) },
          { key: 'open', label: 'Open lead', onSelect: () => navigate(`/leads/${rowMenu.id}`) },
        ] : []} />
        <FilterSheet open={showFilters} onClose={() => setShowFilters(false)} sections={[
          ...filterFields.map(field => ({ key: field.key, label: field.label, kind: 'picker' as const,
            items: [{ value: '', label: `All ${field.label.toLowerCase()}s` }, ...field.items], value: draftFilters[field.key] ?? '', placeholder: `All ${field.label.toLowerCase()}s`,
            onChange: (value: string | null) => setDraftFilters(f => ({ ...f, [field.key]: value || null })) })),
          { key: 'acquisition', label: 'Acquisition', kind: 'chips', options: [...ACQUISITION_OPTIONS], value: draftAcquisition, onChange: (value: AcquisitionFilter) => setDraftAcquisition(value) },
          { key: 'sort', label: 'Sort', kind: 'chips', options: (view === 'pipeline' ? PIPELINE_SORT_OPTIONS : SORT_OPTIONS).flatMap(o => (['asc', 'desc'] as const).map(dir => ({ value: `${o.key}:${dir}`, label: `${o.menuLabel ?? o.label} ${dir === 'asc' ? '↑' : '↓'}` }))),
            value: serializeSort(draftSort), onChange: (value: string) => setDraftSort(parseSort(value) ?? defaultSortFor(view)) },
        ]} resultLabel={`Show ${draftCount} ${draftCount === 1 ? 'lead' : 'leads'}`} onApply={() => { setFilters(draftFilters); setSort(draftSort); setQuery(draftQuery); setAcquisition(draftAcquisition); setShowFilters(false); }}
          onClear={() => { setDraftFilters({ source: null, lead_type: null }); setDraftSort(defaultSortFor(view)); setDraftQuery(''); setDraftAcquisition('all'); }} />
        {stageLead && <LeadStageSheet lead={leads.find(l => l.id === stageLead.id) ?? stageLead} onClose={() => setStageLead(null)} onChanged={refresh} onConvert={() => setConvertLead(stageLead)} />}
        {convertLead && <ConvertLeadModal lead={convertLead} onClose={() => setConvertLead(null)} onConverted={() => { setConvertLead(null); refresh(); }} />}
      </>}
      {lockLead && <LeadStartDateModal lead={lockLead} onClose={() => setLockLead(null)} onLocked={(resurface_date) => {
        setLeads((ls) => ls.map((l) => (l.id === lockLead.id ? { ...l, status: 'locked_in', resurface_date } : l)));
        setLockLead(null);
        refresh();
      }} />}
      {showNew && <NewLeadModal onClose={() => setShowNew(false)} onCreated={refresh} />}
    </>
  );
}

// ── New lead modal (09-new-lead) ─────────────────────────────────────────

export function NewLeadModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const isMobile = useIsMobile();
  const today = melbourneToday();
  const fuOptions = [
    { value: 'none', label: 'None' },
    { value: addDays(today, 1), label: `${formatDate(addDays(today, 1))} · tomorrow` },
    { value: addDays(today, 3), label: `${formatDate(addDays(today, 3))} · +3 days` },
    { value: addDays(today, 7), label: `${formatDate(addDays(today, 7))} · +1 week` },
    { value: addDays(today, 14), label: `${formatDate(addDays(today, 14))} · +2 weeks` },
  ];

  const [clients, setClients] = useState<ClientWithCounts[]>([]);
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [prefilled, setPrefilled] = useState<Record<'contact_name' | 'contact_email' | 'contact_phone', boolean>>({
    contact_name: false,
    contact_email: false,
    contact_phone: false,
  });
  const [form, setForm] = useState({
    name: '',
    client_id: null as string | null,
    company: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    status: 'enquiry' as LeadStatus,
    source: null as string | null,
    request_type: '' as '' | LeadRequestType,
    acquisition: '' as '' | LeadAcquisition,
    campaign: '',
    value: '',
    followup: addDays(today, 7),
    notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [loadingClient, setLoadingClient] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clientPickVersion = useRef(0);
  const contactEditVersions = useRef({ contact_name: 0, contact_email: 0, contact_phone: 0 });
  const sourceEdited = useRef(false);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const setContact = (key: keyof typeof prefilled) => (e: { target: { value: string } }) => {
    contactEditVersions.current[key] += 1;
    setForm((current) => ({ ...current, [key]: e.target.value }));
    setPrefilled((current) => ({ ...current, [key]: false }));
  };

  useEffect(() => {
    api<{ clients: ClientWithCounts[] }>('/api/admin/clients')
      .then((response) => setClients(response.clients))
      .catch(() => {});
  }, []);

  const changeMode = (nextMode: 'new' | 'existing') => {
    clientPickVersion.current += 1;
    setLoadingClient(false);
    setMode(nextMode);
    if (nextMode === 'new') {
      setForm((current) => ({
        ...current,
        client_id: null,
        company: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        source: !sourceEdited.current && current.source === 'repeat_client' ? null : current.source,
      }));
      setPrefilled({ contact_name: false, contact_email: false, contact_phone: false });
    }
  };

  const pickClient = async (clientId: string) => {
    const client = clients.find((candidate) => candidate.id === clientId);
    if (!client) return;
    const pickVersion = ++clientPickVersion.current;
    const editVersions = { ...contactEditVersions.current };
    setLoadingClient(true);
    setError(null);

    setForm((current) => ({
      ...current,
      client_id: client.id,
      company: client.name,
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      source: sourceEdited.current ? current.source : 'repeat_client',
    }));
    setPrefilled({ contact_name: false, contact_email: false, contact_phone: false });

    let contact = {
      name: client.contact_name,
      email: client.contact_email,
      phone: client.contact_phone,
    };
    try {
      const response = await api<{ contacts: ClientContactRow[] }>(`/api/admin/clients/${client.id}/contacts`);
      const defaultContact = response.contacts.find((candidate) => candidate.is_default === 1)
        ?? response.contacts[0];
      if (defaultContact) {
        contact = defaultContact;
      }
    } catch {
      // The legacy columns remain a safe fallback if contacts cannot load.
    }

    if (clientPickVersion.current !== pickVersion) return;

    setForm((current) => {
      const next = { ...current };
      if (contactEditVersions.current.contact_name === editVersions.contact_name) {
        next.contact_name = contact.name ?? '';
      }
      if (contactEditVersions.current.contact_email === editVersions.contact_email) {
        next.contact_email = contact.email ?? '';
      }
      if (contactEditVersions.current.contact_phone === editVersions.contact_phone) {
        next.contact_phone = contact.phone ?? '';
      }
      return next;
    });
    setPrefilled((current) => ({
      contact_name: contactEditVersions.current.contact_name === editVersions.contact_name
        ? Boolean(contact.name)
        : current.contact_name,
      contact_email: contactEditVersions.current.contact_email === editVersions.contact_email
        ? Boolean(contact.email)
        : current.contact_email,
      contact_phone: contactEditVersions.current.contact_phone === editVersions.contact_phone
        ? Boolean(contact.phone)
        : current.contact_phone,
    }));
    setLoadingClient(false);
  };

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!form.name.trim() || busy || loadingClient) return;
    if (mode === 'existing' && !form.client_id) {
      setError('Pick a client to link.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api('/api/admin/leads', {
        method: 'POST',
        json: {
          name: form.name.trim(),
          ...(mode === 'existing' ? { client_id: form.client_id } : {}),
          company: form.company || null,
          contact_name: form.contact_name || null,
          contact_email: form.contact_email || null,
          contact_phone: form.contact_phone || null,
          status: form.status,
          source: form.source,
          request_type: form.request_type || null,
          acquisition: form.acquisition || null,
          // Campaign only rides along with Paid, where the field is shown.
          campaign: form.acquisition === 'paid' ? form.campaign.trim() || null : null,
          value_cents: parseMoney(form.value),
          followup_date: form.followup === 'none' ? null : form.followup,
          notes: form.notes || null,
        },
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create lead.');
      setBusy(false);
    }
  };

  return (
    <Modal
      mobile="full"
      title="New lead"
      context="LEADS"
      onClose={onClose}
      hint="Enquiry date defaults to today"
      foot={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => submit()} disabled={!form.name.trim() || busy || loadingClient}>
            Create lead →
          </button>
        </>
      }
    >
      <form className={isMobile ? "form-grid lead-new-form" : "form-grid"} onSubmit={submit}>
        <div className="f-field span-2">
          <span className="f-label">Client</span>
          <Seg
            options={[
              { value: 'new', label: 'New client' },
              { value: 'existing', label: 'Existing client' },
            ]}
            value={mode}
            onChange={changeMode}
          />
        </div>
        <div className="f-field span-2">
          <span className="f-label">
            Lead name <span className="req">*</span>
          </span>
          <input className="f-input" value={form.name} onChange={set('name')} autoFocus />
        </div>
        {mode === 'new' ? (
          <div className="f-field">
            <span className="f-label">Company</span>
            <input
              className="f-input"
              value={form.company}
              onChange={set('company')}
              placeholder="e.g. Fielder's Bakehouse"
            />
          </div>
        ) : (
          <div className="f-field">
            <span className="f-label">Existing client</span>
            <SelectField label="Existing client"
              items={clients.map((client) => ({ value: client.id, label: client.name }))}
              value={form.client_id}
              onChange={pickClient}
              placeholder="Pick a client…"
              searchable
            />
          </div>
        )}
        <div className="f-field">
          <span className="f-label">
            Contact name {prefilled.contact_name && <span className="f-from">from client</span>}
          </span>
          <input
            className={`f-input${prefilled.contact_name ? ' prefilled' : ''}`}
            value={form.contact_name}
            onChange={setContact('contact_name')}
            placeholder="Who you're talking to"
          />
        </div>
        <div className={isMobile ? "f-field lead-pair" : "f-field"}>
          <span className="f-label">
            Email {prefilled.contact_email && <span className="f-from">from client</span>}
          </span>
          <input
            className={`f-input mono${prefilled.contact_email ? ' prefilled' : ''}`}
            type={isMobile ? "email" : undefined}
            value={form.contact_email}
            onChange={setContact('contact_email')}
            placeholder="name@company.com.au"
          />
        </div>
        <div className={isMobile ? "f-field lead-pair" : "f-field"}>
          <span className="f-label">
            Phone {prefilled.contact_phone && <span className="f-from">from client</span>}
          </span>
          <input
            className={`f-input mono${prefilled.contact_phone ? ' prefilled' : ''}`}
            type={isMobile ? "tel" : undefined}
            value={form.contact_phone}
            onChange={setContact('contact_phone')}
            placeholder="04…"
          />
        </div>
        <div className={isMobile ? "f-field lead-pair" : "f-field"}>
          <span className="f-label">Status</span>
          <SelectField label="Status"
            items={LEAD_OPEN_STATUSES.map((s) => ({ value: s, label: LEAD_STATUS_LABELS[s] }))}
            value={form.status}
            onChange={(v) => setForm((f) => ({ ...f, status: v }))}
          />
        </div>
        <div className={isMobile ? "f-field lead-pair" : "f-field"}>
          <span className="f-label">Source</span>
          <SelectField label="Source"
            items={LEAD_SOURCES.map((s) => ({ value: s, label: LEAD_SOURCE_LABELS[s] }))}
            value={form.source as (typeof LEAD_SOURCES)[number] | null}
            onChange={(v) => {
              sourceEdited.current = true;
              setForm((f) => ({ ...f, source: v }));
            }}
            placeholder="Select source…"
          />
        </div>
        <div className="f-field">
          <span className="f-label">Request</span>
          <Seg
            options={[{ value: 'quote', label: 'Quote' }, { value: 'proposal', label: 'Proposal' }, { value: '', label: 'Not set' }] as const}
            value={form.request_type}
            onChange={(request_type) => setForm((f) => ({ ...f, request_type }))}
          />
        </div>
        <div className="f-field">
          <span className="f-label">Acquisition</span>
          <Seg
            options={[...LEAD_ACQUISITIONS.map((value) => ({ value, label: LEAD_ACQUISITION_LABELS[value] })), { value: '' as const, label: 'Not set' }]}
            value={form.acquisition}
            onChange={(acquisition) => setForm((f) => ({ ...f, acquisition }))}
          />
        </div>
        {form.acquisition === 'paid' && (
          <div className="f-field span-2">
            <span className="f-label">Campaign</span>
            <input className="f-input" value={form.campaign} onChange={set('campaign')} maxLength={LEAD_CAMPAIGN_MAX} placeholder="e.g. Spring clinic promo · Lead form" />
          </div>
        )}
        <div className="f-field">
          <span className="f-label">Estimated value</span>
          <input className="f-input money" value={form.value} onChange={set('value')} placeholder="$0" />
        </div>
        <div className="f-field">
          <span className="f-label">Follow up</span>
          {isMobile ? <div className="chiprow lead-followup-chips">{fuOptions.map((option, index) => <button type="button" key={option.value} className={`c${form.followup === option.value ? ' on' : ''}`} onClick={() => setForm(f => ({ ...f, followup: option.value }))}>{['None', 'Tomorrow', '+3 days', '+1 week', '+2 weeks'][index]}</button>)}</div> : <SelectField
            items={fuOptions}
            value={form.followup}
            onChange={(v) => setForm((f) => ({ ...f, followup: v }))}
            mono
          />}
        </div>
        <div className="f-field span-2">
          <span className="f-label">Notes</span>
          <textarea
            className="f-textarea"
            value={form.notes}
            onChange={set('notes')}
            placeholder="What they asked for, budget signals, next step…"
          />
        </div>
        {error && <p className="error-text span-2">{error}</p>}
      </form>
    </Modal>
  );
}
